import { ShareConfig, DEFAULT_CONFIG, PodcastConfig, PodcastLinks } from './types';
import { youtubeIcon, appleIcon, spotifyIcon, soundcloudIcon } from './icons';
import { isMobileDevice } from '../../lib/utils/window-utils';

const createCentralTooltip = (): HTMLElement => {
    const tooltip = document.createElement('div');
    tooltip.className = 'central-tooltip';
    document.body.appendChild(tooltip);

    return tooltip;
};

const initializeShareButtons = (config: ShareConfig = DEFAULT_CONFIG): void => {
    const centralTooltip = createCentralTooltip();
    const shareButtons = document.querySelectorAll<HTMLElement>('.share-icon-wrapper');
    const baseUrl = window.location.origin;
    const isMobile = isMobileDevice();

    const handleShare = async (button: HTMLElement): Promise<void> => {
        const relativeUrl = button.dataset.url;
        if (!relativeUrl) return;
        
        const absoluteUrl = new URL(relativeUrl, baseUrl).href;

        if (isMobile && navigator.share) {
            try {
                await navigator.share({
                    url: absoluteUrl
                });
            } catch (err) {
                console.error('Share failed:', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(absoluteUrl);
                showCopySuccess(centralTooltip, config);
            } catch (error) {
                console.error('Failed to copy:', error);
            }
        }
    };

    shareButtons.forEach(button => {
        if (isMobile) {
            let tooltipTimer: NodeJS.Timeout;
            
            button.addEventListener('touchstart', function(this: HTMLElement, e: TouchEvent) {
                e.preventDefault();
                
                const currentTooltip = this.querySelector<HTMLElement>('.tooltip');
                if (currentTooltip) {
                    currentTooltip.style.opacity = '1';
                    currentTooltip.style.visibility = 'visible';
                    
                    clearTimeout(tooltipTimer);
                    tooltipTimer = setTimeout(() => {
                        currentTooltip.style.opacity = '0';
                        currentTooltip.style.visibility = 'hidden';
                    }, 2000);
                }
                
                handleShare(this);
            });
        } else {
            button.addEventListener('click', (e: Event) => {
                e.preventDefault();
                handleShare(button);
            });
        }
    });
};

const initializePodcastButtons = (): void => {
    const buttons = document.querySelectorAll<HTMLElement>('.podcast-button-wrapper');
    
    buttons.forEach(button => {
        const tooltip = button.querySelector('.podcast-social-tooltip') as HTMLElement;
        if (!tooltip) return;
        
        const isHoverableDevice = window.matchMedia('(hover: hover)').matches;

        if (isHoverableDevice) {
            button.addEventListener('mouseenter', () => {
                tooltip.classList.add('visible');
            });

            button.addEventListener('mouseleave', () => {
                tooltip.classList.remove('visible');
            });
          } else {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                tooltip.classList.toggle('visible');
            });

            document.addEventListener('click', (event: Event) => {
                const target = event.target as HTMLElement;
                if (!button.contains(target)) {
                    tooltip.classList.remove('visible');
                }
            });
        }
    });
};

const renderPodcastButtons = (links: PodcastLinks) => {
    const podcastLinks = Object.entries(links)
        .filter(([_, value]) => value !== null)
        .map(([platform, url]) => `
            <a href="${url}" target="_blank" class="social-button ${platform}">
                ${getIcon(platform)}
            </a>
        `).join('');

    return `<div class="podcast-social-tooltip">
                <div class="tooltip-title">Listen On</div>
                <div class="social-buttons">
                    ${podcastLinks}
                </div>
            </div>`
}

const getIcon = (platform: string): string => {
    switch (platform) {
        case 'YouTube': return youtubeIcon();
        case 'Apple': return appleIcon();
        case 'Spotify': return spotifyIcon();
        case 'SoundCloud': return soundcloudIcon();
        default: return '';
    }
}

const showCopySuccess = (
    tooltip: HTMLElement, 
    { successText, duration }: ShareConfig
): void => {
    tooltip.textContent = successText;
    tooltip.classList.add('visible');

    setTimeout(() => {
        tooltip.classList.remove('visible');
    }, duration);
};

export { initializeShareButtons, initializePodcastButtons, renderPodcastButtons };